import {
  ChronoField,
  ChronoUnit,
  convert,
  DateTimeFormatter,
  DayOfWeek,
  Duration,
  Instant,
  IsoFields,
  LocalDate,
  LocalDateTime,
  YearMonth,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import "@js-joda/timezone";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { ComponentContextProps } from "components/fragments/dashboard/components/context/ComponentContextWrapper";
import { errorToaster } from "components/pages/measurement-devices/utils/utils";
import { ColumnSortEvent } from "primereact/column";
import { T } from "services/lang/LanguageService";
import {
  DATE_FORMATTER_DAYS,
  DATE_FORMATTER_HOURS,
  DATE_FORMATTER_MINUTES,
  DATE_FORMATTER_MONTHS,
  DATE_FORMATTER_SECONDS,
  DATE_FORMATTER_YEARS,
} from "./constants";
import { DweenCalendarEnum, DweenTimeUnit, DweenTimeUnitEnum } from "./enums";
import { WithSameNullability } from "./generics";

export const BROWSER_ZONE_ID = ZoneId.systemDefault();
export const LT_ZONE_ID = ZoneId.of("Europe/Vilnius");
export const UTC_ZONE_ID = ZoneId.of("UTC");

export type PossibleDateTypes = ZonedDateTime | Instant | Date | string | number;
export type PossibleDateTypesOrNull = PossibleDateTypes | null | undefined;
export type PossibleTimeUnitTypes = ChronoUnit | DweenTimeUnitEnum | DweenTimeUnit;

export function durationSeconds(duration: number, durationUnit: string): number {
  switch (durationUnit) {
    case "SECONDS":
      return duration;
    case "MINUTES":
      return duration * 60;
    case "HOURS":
      return duration * 60 * 60;
    case "DAYS":
      return duration * 60 * 60 * 24;
    case "WEEKS":
      return duration * 60 * 60 * 24 * 7;
    case "MONTHS":
      return duration * 60 * 60 * 24 * 7 * 30;
    default:
      throw new Error(T("error.not-known-time-unit"));
  }
}

export function formatDuration(secs: number) {
  let fmt = "";
  secs = Math.abs(secs);

  const days = Math.floor(secs / (3600 * 24));
  const hours = Math.floor((secs - days * 3600 * 24) / 3600);
  const minutes = Math.floor((secs - days * 3600 * 24 - hours * 3600) / 60);
  const seconds = secs - days * 3600 * 24 - hours * 3600 - minutes * 60;

  if (days > 0) {
    fmt += `${days}d `;
  }
  if (hours > 0) {
    fmt += `${hours}h `;
  }
  if (minutes > 0) {
    fmt += `${minutes}m `;
  }
  if (seconds >= 0) {
    fmt += `${seconds.toFixed(0)}s`;
  }

  return fmt;
}

export function getDiffSeconds(date1: Date | string, date2: Date | string): number {
  const d1 = new Date(toNativeDate(date1)!);
  const d2 = new Date(toNativeDate(date2)!);

  return (+d1 - +d2) / 1000;
}

export function getDiffSecondsInstant(date1: Instant, date2: Instant): number {
  return (date1.toEpochMilli() - date2.toEpochMilli()) / 1000;
}

export const toInstantOrNow = (value?: PossibleDateTypes): Instant => {
  if (value == null) return Instant.now();

  return toInstant(value);
};

export const toInstant = <T extends PossibleDateTypesOrNull>(value?: T): WithSameNullability<T, Instant> => {
  if (!value) return null as WithSameNullability<T, Instant>;

  if (value instanceof Instant) return value;

  if (typeof value === "string") {
    try {
      return Instant.parse(value);
    } catch (error) {
      const parsed = new Date(value).toInstant();
      console.warn("Failed to parse string to Instant, defaulting to Date parsing", value);
      return parsed;
    }
  }

  if (value instanceof Date) {
    return value.toInstant();
  }

  if (value instanceof ZonedDateTime) {
    return value.toInstant();
  }

  if (typeof value === "number") {
    return Instant.ofEpochMilli(value);
  }

  throw new Error(`${T("error.invalid-instant-type")} ${value} (${typeof value})`);
};

export const toChronoUnit = (unit: any): ChronoUnit => {
  if (typeof unit === "object") {
    const { _name: name } = unit;
    return ChronoUnit[name?.toUpperCase() as keyof typeof ChronoUnit] || null;
  }
  const resolvedUnit = ChronoUnit[unit?.toString().toUpperCase() as keyof typeof ChronoUnit];
  return resolvedUnit || null;
};

export const fmtDateUntilUnit = (
  date: PossibleDateTypes,
  formatUntil: DweenTimeUnitEnum | undefined,
  zoneId: ZoneId
) => {
  const displayDate = toZonedDateTime(date, zoneId);
  const unit = formatUntil ? toChronoUnit(formatUntil) : ChronoUnit.SECONDS;

  switch (unit) {
    case ChronoUnit.SECONDS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_SECONDS);
    case ChronoUnit.MINUTES:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_MINUTES);
    case ChronoUnit.HOURS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_HOURS);
    case ChronoUnit.DAYS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_DAYS);
    case ChronoUnit.WEEKS: {
      const week = displayDate.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      return `${T("letter.week-first-letter")} ${week}`;
    }
    case ChronoUnit.MONTHS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_MONTHS);
    case ChronoUnit.YEARS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_YEARS);

    default:
      throw Error(`Not supported for ${formatUntil}`);
  }
};

// old project/components use LT timezone for datasets
// this is for companies which have bad timezone
export const fmtDateUntilUnitLtCompatible = (
  date: PossibleDateTypes,
  formatUntil: DweenTimeUnitEnum | undefined,
  zoneId: ZoneId,
  forceLtTz: boolean
) => {
  if (forceLtTz) {
    return fmtDateUntilUnit(date, formatUntil, LT_ZONE_ID);
  }

  return fmtDateUntilUnit(date, formatUntil, zoneId);
};

export function fmtDatetimeAdvanced(
  date: PossibleDateTypes | undefined | null,
  zoneId: ZoneId,
  conf?: {
    defaultToNow?: boolean;
    companyZoneId?: ZoneId;
    pattern?: DateTimeFormatter | string;
    showOffset?: boolean;
  }
): string {
  const usedConf = conf || {};
  if (!date && !usedConf.defaultToNow) return "";

  if (usedConf?.pattern instanceof DateTimeFormatter) {
    usedConf.pattern = usedConf.pattern || DATE_FORMATTER_MINUTES;
  } else {
    usedConf.pattern = usedConf.pattern
      ? DateTimeFormatter.ofPattern(usedConf.pattern)
      : DATE_FORMATTER_MINUTES;
  }

  const zonedTime = toZonedDateTime(date || Instant.now(), zoneId);

  const localDateTime = zonedTime.toLocalDateTime();
  let dateStr = localDateTime.format(usedConf.pattern);
  if (usedConf.showOffset) {
    const offset = zonedTime.offset().toString();
    dateStr += ` [${offset}]`;
  }
  return dateStr;
}

export function fmtDatetime(
  date: PossibleDateTypes | undefined | null,
  displayZoneId: ZoneId,
  pattern: string | DateTimeFormatter | undefined = undefined,
  showOffset: boolean = false,
  companyZoneId?: ZoneId | undefined
) {
  return fmtDatetimeAdvanced(date, displayZoneId, {
    defaultToNow: false,
    companyZoneId,
    pattern,
    showOffset,
  });
}

export namespace JodaTimeUtils {
  const toDweenUnit = (unit: any): DweenTimeUnitEnum => {
    if (typeof unit === "object") {
      const { _name: name } = unit;
      return DweenTimeUnitEnum[name?.toUpperCase() as keyof typeof DweenTimeUnitEnum] || null;
    }
    const resolvedUnit = DweenTimeUnitEnum[unit?.toString().toUpperCase() as keyof typeof DweenTimeUnitEnum];
    return resolvedUnit || null;
  };

  const validDweenTimeUnit = (unit: any): unit is DweenTimeUnitEnum => {
    const dweenUnit = toDweenUnit(unit);
    return dweenUnit != null;
  };

  export const truncateToZoned = (
    date: PossibleDateTypes,
    unit: PossibleTimeUnitTypes | DweenCalendarEnum | undefined,
    zoneId: ZoneId
  ): ZonedDateTime => {
    if (zoneId == null) {
      throw new Error("ZoneId must be provided.");
    }

    if (unit == null || unit === DweenCalendarEnum.CALENDAR_445) {
      return toZonedDateTime(date, zoneId);
    }

    if (!validDweenTimeUnit(unit)) {
      throw new Error("Unhandled time unit.");
    }
    unit = toDweenUnit(unit);

    const tempDate = toZonedDateTime(date, zoneId);

    switch (unit) {
      case DweenTimeUnitEnum.MILLIS:
      case DweenTimeUnitEnum.SECONDS:
      case DweenTimeUnitEnum.MINUTES:
      case DweenTimeUnitEnum.HOURS:
      case DweenTimeUnitEnum.DAYS:
        return tempDate.truncatedTo(toChronoUnit(unit));
      case DweenTimeUnitEnum.WEEKS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).with(DayOfWeek.MONDAY);
      case DweenTimeUnitEnum.MONTHS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfMonth(1);
      case DweenTimeUnitEnum.YEARS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfYear(1);

      case DweenTimeUnitEnum.HALFHOUR:
        return tempDate
          .truncatedTo(ChronoUnit.HOURS)
          .plus(tempDate.minute() >= 30 ? 30 : 0, ChronoUnit.MINUTES);
      default:
        throw new Error("Unhandled time unit.");
    }
  };

  export const CeilingTo = (date: PossibleDateTypes, unit: ChronoUnit, zoneId: ZoneId): Instant => {
    if (!validDweenTimeUnit(unit)) {
      throw new Error("Unhandled time unit.");
    }

    const zonedDate = toZonedDateTime(date, zoneId);
    const truncatedDate = truncateToZoned(date, unit, zoneId);

    let truncated: ZonedDateTime | null = null;
    switch (unit) {
      case ChronoUnit.MILLIS:
      case ChronoUnit.SECONDS:
      case ChronoUnit.MINUTES:
      case ChronoUnit.HOURS:
      case ChronoUnit.DAYS:
      case ChronoUnit.WEEKS:
      case ChronoUnit.MONTHS:
      case ChronoUnit.YEARS:
        if (truncatedDate.isBefore(zonedDate)) {
          truncated = truncatedDate.plus(1, unit);
        } else {
          truncated = truncatedDate;
        }
        break;
      default:
        throw new Error("Unhandled time unit.");
    }

    return truncated?.toInstant();
  };

  export const truncateAndOffsetLocalDatetime = (
    date: LocalDateTime,
    truncateToUnit: DweenTimeUnit,
    offset: number,
    offsetUnit: DweenTimeUnit
  ): LocalDateTime => {
    let truncatedDate: LocalDateTime;
    switch (truncateToUnit) {
      case DweenTimeUnitEnum.WEEKS:
        truncatedDate = date.with(DayOfWeek.MONDAY).truncatedTo(ChronoUnit.DAYS);
        break;
      case DweenTimeUnitEnum.MONTHS:
        truncatedDate = date.withDayOfMonth(1).truncatedTo(ChronoUnit.DAYS);
        break;
      case DweenTimeUnitEnum.YEARS:
        truncatedDate = date.withDayOfYear(1).truncatedTo(ChronoUnit.DAYS);
        break;
      default:
        truncatedDate = date.truncatedTo(toChronoUnit(truncateToUnit));
    }
    return truncatedDate.plus(offset, toChronoUnit(offsetUnit));
  };
}

export function truncateDate(
  date: PossibleDateTypes,
  truncateTo: PossibleTimeUnitTypes | undefined,
  zoneId: ZoneId
): Date {
  return JodaTimeUtils.truncateToZoned(date, truncateTo, zoneId).toDate();
}

export function addToDate(
  date: Date | Instant | ZonedDateTime | string,
  amount: number,
  unit: DweenTimeUnitEnum | String | undefined
): Date {
  const d = toNativeDate(date);

  if (amount == null || unit == null) {
    return d;
  }

  switch (unit) {
    case DweenTimeUnitEnum.SECONDS:
      d.setSeconds(d.getSeconds() + amount);
      break;
    case DweenTimeUnitEnum.MINUTES:
      d.setMinutes(d.getMinutes() + amount);
      break;
    case DweenTimeUnitEnum.HALFHOUR:
      d.setMinutes(d.getMinutes() + amount * 30);
      break;
    case DweenTimeUnitEnum.HOURS:
      d.setHours(d.getHours() + amount);
      break;
    case DweenTimeUnitEnum.DAYS:
      d.setHours(d.getHours() + amount * 24);
      break;
    case DweenTimeUnitEnum.WEEKS:
      d.setHours(d.getHours() + amount * 24 * 7);
      break;
    case DweenTimeUnitEnum.MONTHS:
      d.setMonth(d.getMonth() + amount);
      break;
    case DweenTimeUnitEnum.YEARS:
      d.setFullYear(d.getFullYear() + amount);
      break;
    default:
      break;
  }
  return d;
}

export function addToInstant(
  date: PossibleDateTypes,
  amount: number,
  unit: DweenTimeUnitEnum | string | undefined,
  zoneId: ZoneId
): Instant {
  const d = toZonedDateTime(date, zoneId);
  if (amount == null || unit == null) {
    return d.toInstant();
  }

  return addToZonedDateTime(d, amount, unit).toInstant();
}

export function addToZonedDateTime(
  date: ZonedDateTime,
  amount: number,
  unit: DweenTimeUnitEnum | string | undefined
): ZonedDateTime {
  if (amount == null || unit == null) return date;

  switch (unit) {
    case DweenTimeUnitEnum.SECONDS:
      return date.plus(amount, ChronoUnit.SECONDS);
    case DweenTimeUnitEnum.MINUTES:
      return date.plus(amount, ChronoUnit.MINUTES);
    case DweenTimeUnitEnum.HALFHOUR:
      return date.plus(amount * 30, ChronoUnit.MINUTES);
    case DweenTimeUnitEnum.HOURS:
      return date.plus(amount, ChronoUnit.HOURS);
    case DweenTimeUnitEnum.DAYS:
      return date.plus(amount, ChronoUnit.DAYS);
    case DweenTimeUnitEnum.WEEKS:
      return date.plus(amount, ChronoUnit.WEEKS);
    case DweenTimeUnitEnum.MONTHS:
      return date.plus(amount, ChronoUnit.MONTHS);
    case DweenTimeUnitEnum.YEARS:
      return date.plus(amount, ChronoUnit.YEARS);
    default:
      return date;
  }
}

export function primereactDataTableSortDateFunction(e: ColumnSortEvent) {
  if (e.data) {
    const orderMultiplier = e.order || 1;
    return [...e.data].sort((a, b) => {
      const valA = a.startsAt || a.start ? new Date(a.startsAt || a.start).getTime() : NaN;
      const valB = b.startsAt || b.start ? new Date(b.startsAt || b.start).getTime() : NaN;

      if (Number.isNaN(valA) && !Number.isNaN(valB)) {
        return -1 * orderMultiplier;
      }
      if (!Number.isNaN(valA) && Number.isNaN(valB)) {
        return orderMultiplier;
      }
      return (valA - valB) * orderMultiplier;
    });
  }

  return [];
}

function getUnitFromSeconds(seconds: number) {
  if (seconds < 60) {
    return ChronoUnit.SECONDS;
  }
  if (seconds < 3600) {
    return ChronoUnit.MINUTES;
  }
  if (seconds < 86400) {
    return ChronoUnit.HOURS;
  }
  return ChronoUnit.DAYS;
}

function getUnitLangKey(unit: ChronoUnit) {
  switch (unit) {
    case ChronoUnit.SECONDS:
      return "time.seconds";
    case ChronoUnit.MINUTES:
      return "time.minutes";
    case ChronoUnit.HOURS:
      return "time.hours";
    case ChronoUnit.DAYS:
      return "time.days";
    default:
      return "time.seconds";
  }
}

export function timeAgo(date: Date | string | undefined | null) {
  if (!date) return "";

  const secondsAgo = timeDifference(toInstant(date));
  const unit = getUnitFromSeconds(secondsAgo);
  const unitLangKey = getUnitLangKey(unit);

  let count;
  switch (unit) {
    case ChronoUnit.SECONDS:
      count = secondsAgo;
      break;
    case ChronoUnit.MINUTES:
      count = Math.round(secondsAgo / 60);
      break;
    case ChronoUnit.HOURS:
      count = Math.round(secondsAgo / 3600);
      break;
    case ChronoUnit.DAYS:
      count = Math.round(secondsAgo / 86400);
      break;
    default:
      count = secondsAgo;
  }

  return T("message.time-ago", { count, unit: T(unitLangKey) });
}

export const timeDifference = (from: Instant, to?: Instant) => {
  const dateTo = to ?? Instant.now();
  const duration = Duration.between(from, dateTo).seconds();
  return Math.abs(duration);
};

export const dateBody = (date: string, dateFormat: DweenTimeUnitEnum, zoneId: ZoneId): string => {
  const dateDate = new Date(date);

  return fmtDateUntilUnit(dateDate, dateFormat, zoneId);
};

export const secondsToTimeSpan = (seconds: number) => {
  const timeSpan = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
  if (!seconds || typeof seconds !== "number" || seconds <= 0) return timeSpan;

  timeSpan.seconds = seconds;
  if (seconds < 60) return timeSpan;

  timeSpan.minutes = Math.floor(seconds / 60);
  timeSpan.seconds %= 60;
  if (timeSpan.minutes < 60) return timeSpan;

  timeSpan.hours = Math.floor(timeSpan.minutes / 60);
  timeSpan.minutes %= 60;
  if (timeSpan.hours < 24) return timeSpan;

  timeSpan.days = Math.floor(timeSpan.hours / 24);
  timeSpan.hours %= 24;
  return timeSpan;
};

export type TimeSpan = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const timeSpanToSeconds = (timeSpan: TimeSpan) => {
  if (!timeSpan || !(timeSpan instanceof Object) || Object.keys(timeSpan).length !== 4) return 0;

  const days = Math.abs(timeSpan.days) * 86400;
  const hours = Math.abs(timeSpan.hours) * 3600;
  const minutes = Math.abs(timeSpan.minutes) * 60;
  const seconds = Math.abs(timeSpan.seconds);
  return days + hours + minutes + seconds;
};

export const convertTimeSpanToString = (timeSpan: TimeSpan) => {
  if (!timeSpan) return "-";
  let timeString = "";

  if (timeSpan.days) {
    timeString += `${timeSpan.days}d `;
  }
  if (timeSpan.hours) {
    timeString += `${timeSpan.hours}h `;
  }
  if (timeSpan.minutes) {
    timeString += `${timeSpan.minutes}min `;
  }
  if (timeSpan.seconds) {
    timeString += `${timeSpan.seconds}s `;
  }
  return timeString.trim() || "-";
};

export const convertStringToTimeSpan = (timeSpanString: string) => {
  const timeSpan = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (!timeSpanString || timeSpanString === "-") return timeSpan;

  const timeSpans = timeSpanString.split(" ");

  timeSpans.forEach(ts => {
    if (ts.includes("d")) {
      timeSpan.days = parseInt(ts.replace("d", ""), 10);
    } else if (ts.includes("h")) {
      timeSpan.hours = parseInt(ts.replace("h", ""), 10);
    } else if (ts.includes("min")) {
      timeSpan.minutes = parseInt(ts.replace("min", ""), 10);
    } else if (ts.includes("s")) {
      timeSpan.seconds = parseInt(ts.replace("s", ""), 10);
    }
  });

  return timeSpan;
};

export const formatToIsoStringWithoutMillis = (instant: Instant): string => {
  const truncated = instant.truncatedTo(ChronoUnit.SECONDS);
  return truncated.toString();
};

export const timeDuration = (start: string | undefined, end: string | undefined) => {
  if (!start || !end) return 0;
  return Duration.between(toInstant(start), toInstant(end)).seconds();
};

export const formatDateDuration = (start: string | undefined, end: string | undefined) => {
  if (!start || !end) return "-";
  let result;
  try {
    const offset = timeDuration(start, end);
    result = convertTimeSpanToString(secondsToTimeSpan(offset));
  } catch (error) {
    if (error instanceof Error) {
      result = error.message;
    }
    errorToaster(`Failed to parse "${start}" or "${end}" into date`);
  }
  return result;
};

export const getTimestamp = (val: unknown): number | null => {
  if (typeof val === "string" || typeof val === "number") {
    const date = new Date(val);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }
  return null;
};

export const getRelativeOffsetForGroup = (
  groupStart: string,
  startTimeStr?: string,
  endTimeStr?: string
): [number, number] => {
  if (!startTimeStr || !groupStart) return [0, 0];
  const groupStartTime = new Date(groupStart).getTime();
  const start = new Date(startTimeStr).getTime();
  const end = endTimeStr ? new Date(endTimeStr).getTime() : start;
  return [start - groupStartTime, end - groupStartTime];
};

// Dont use if not needed
// Coerces date to to display same local time as targetTz (timestamp changes!!!)
export const coerceDateToTimezone = (d: Date | undefined | null, targetTz: ZoneId) => {
  if (!d) return null;

  const date = toZonedDateTime(d, targetTz).withZoneSameLocal(BROWSER_ZONE_ID).toDate();

  return date;
};

// Dont use if not needed
// Coerces date back to browser local time (fixes timestamp from coerceDateToDisplay changes)
export const coerceDateToBrowser = (d: Date | undefined, targetTz: ZoneId) => {
  if (!d) return null;

  const date = toZonedDateTime(d, BROWSER_ZONE_ID).withZoneSameLocal(targetTz).toDate();

  return date;
};

export const isSameEpochTime = (
  date1: PossibleDateTypes | undefined,
  date2: PossibleDateTypes | undefined
) => {
  const i1 = date1 ? toInstant(date1) : undefined;
  const i2 = date2 ? toInstant(date2) : undefined;
  return i1?.toEpochMilli() === i2?.toEpochMilli();
};

export const toLocalDateTime = <T extends string | undefined | null>(
  value: T
): WithSameNullability<T, LocalDateTime> => {
  if (!value) return null as WithSameNullability<T, LocalDateTime>;

  try {
    return LocalDateTime.parse(value) as WithSameNullability<T, LocalDateTime>;
  } catch (error) {
    console.warn("Failed to parse string to LocalDateTime", value);
    return null as WithSameNullability<T, LocalDateTime>;
  }
};

export const toZonedDateTime = <T extends PossibleDateTypesOrNull | LocalDateTime>(
  value: T,
  zoneId: ZoneId
): WithSameNullability<T, ZonedDateTime> => {
  if (value instanceof LocalDateTime) {
    return value.atZone(zoneId);
  }

  return toInstant(value)?.atZone(zoneId) as WithSameNullability<T, ZonedDateTime>;
};

export const toZonedDateTimeOrNow = (tz: ZoneId, refTime: PossibleDateTypesOrNull): ZonedDateTime => {
  if (refTime) {
    return toZonedDateTime(refTime, tz);
  }
  return ZonedDateTime.now(tz);
};

export const toNativeDateOrNow = (
  date: Instant | ZonedDateTime | Date | string | number | undefined
): Date => {
  if (!date) return new Date();

  return toNativeDate(date);
};

export const toNativeDate = (date: Instant | ZonedDateTime | Date | string | number): Date => {
  if (date instanceof Date) return new Date(date);

  if (date instanceof Instant) {
    return convert(date).toDate();
  }

  if (date instanceof ZonedDateTime) {
    return convert(date).toDate();
  }

  if (typeof date === "number") {
    return new Date(date);
  }

  try {
    return new Date(Date.parse(date));
  } catch {
    throw new Error(`${T("error.invalid-instant-type")} ${date} (${typeof date})`);
  }
};

export const getStartOfYesterday = (zoneId: ZoneId): Date => {
  return ZonedDateTime.now(zoneId).truncatedTo(ChronoUnit.DAYS).minusDays(1).toDate();
};

export const getEndOfYesterday = (zoneId: ZoneId): Date => {
  return ZonedDateTime.now(zoneId).truncatedTo(ChronoUnit.DAYS).minusSeconds(1).toDate();
};

export const checkIfDataInCurrentMonthExists = (valueDate: string, currentTz: ZoneId, refTime?: Instant) => {
  const zdt = toZonedDateTime(valueDate, currentTz);
  const ref = toZonedDateTimeOrNow(currentTz, refTime);

  const startOfCurrentMonth = ref.withDayOfMonth(1).truncatedTo(ChronoUnit.DAYS);
  const startOfNextMonth = ref.plusMonths(1).withDayOfMonth(1).truncatedTo(ChronoUnit.DAYS);

  return (
    (zdt.isAfter(startOfCurrentMonth) || zdt.isEqual(startOfCurrentMonth)) && zdt.isBefore(startOfNextMonth)
  );
};

export const checkIfDataInLastFinishedWeekExists = (
  valueDate: string,
  currentTz: ZoneId,
  refTime?: Instant
) => {
  const zdt = toZonedDateTime(valueDate, currentTz);

  const now = toZonedDateTimeOrNow(currentTz, refTime);
  const lastWeek = now.minusWeeks(1);

  const startOfLastWeek = lastWeek.with(ChronoField.DAY_OF_WEEK, 1).truncatedTo(ChronoUnit.DAYS);

  const startOfThisWeek = now.with(ChronoField.DAY_OF_WEEK, 1).truncatedTo(ChronoUnit.DAYS);

  return (zdt.isAfter(startOfLastWeek) || zdt.isEqual(startOfLastWeek)) && zdt.isBefore(startOfThisWeek);
};

export const getMonthValuesForYears = (
  dataset: InstantDoubleValue[],
  timeZone: ZoneId,
  refTime?: Instant
): InstantDoubleValue[] => {
  if (!dataset?.length) return [];

  const nowLocal = toZonedDateTimeOrNow(timeZone, refTime);
  const currentYm = YearMonth.from(nowLocal);

  const results = dataset
    .filter(entry => {
      try {
        const ym = YearMonth.from(ZonedDateTime.parse(entry.key).withZoneSameInstant(timeZone));
        return ym.monthValue() === currentYm.monthValue();
      } catch {
        return false;
      }
    })
    .sort((a, b) => a.key.localeCompare(b.key));

  return results;
};

export const calculateDownsampleInterval = (from: Instant, to: Instant) => {
  const intervalFromToInSeconds = from.until(to, ChronoUnit.SECONDS);
  const maxPoints = 500;
  return Math.round(intervalFromToInSeconds / maxPoints);
};

let cachedFallbackInstant: Instant | null = null;

export const getComponentRefTime = (props: ComponentContextProps): Instant => {
  if (props.instantReferenceTime) return props.instantReferenceTime;
  if (props.referenceTime) return toInstant(props.referenceTime);

  if (!cachedFallbackInstant) {
    cachedFallbackInstant = Instant.now();
  }
  return cachedFallbackInstant;
};

export const computeAge = (loadedTime: Instant | undefined, time: Instant) => {
  if (!loadedTime || !time) return null;
  const loaded = toInstant(loadedTime);
  const diffSec = getDiffSecondsInstant(loaded, time);
  const duration = formatDuration(diffSec);

  return loaded.isAfter(time) ? `(${T("word.age")}: ${duration})` : `(${T("word.age")}: - ${duration})`;
};

export const getSeconds = (unit: DweenTimeUnit) => {
  switch (unit) {
    case DweenTimeUnitEnum.SECONDS:
      return 1;
    case DweenTimeUnitEnum.MINUTES:
      return 60;
    case DweenTimeUnitEnum.HALFHOUR:
      return 30 * 60;
    case DweenTimeUnitEnum.HOURS:
      return 60 * 60;
    case DweenTimeUnitEnum.DAYS:
      return 60 * 60 * 24;
    case DweenTimeUnitEnum.WEEKS:
      return 60 * 60 * 24 * 7;
    case DweenTimeUnitEnum.MONTHS:
      return 60 * 60 * 24 * 7 * 30;
    case DweenTimeUnitEnum.YEARS:
      return 60 * 60 * 24 * 7 * 30 * 365;
    default:
      throw new Error(T("error.not-known-time-unit"));
  }
};

export const formatTimeHHmm = (dateTime: ZonedDateTime): string => {
  return `${String(dateTime.hour()).padStart(2, "0")}:${String(dateTime.minute()).padStart(2, "0")}`;
};

/**
 * Get the instant of the year beginning for a given year
 * @param {number} year - The year (e.g., 2024)
 * @param {ZoneId} zoneId - Optional timezone (default: ZoneId.UTC)
 * @returns {Instant} The instant at the start of the year
 */
export const getYearBeginningInstant = (year: number, zoneId = ZoneId.UTC) => {
  const yearStart = LocalDate.of(year, 1, 1);
  const zonedDateTime = yearStart.atStartOfDay(zoneId);
  return zonedDateTime.toInstant();
};

/**
 * Get the instant of the year end for a given year
 * @param {number} year - The year (e.g., 2024)
 * @param {ZoneId} zoneId - Optional timezone (default: ZoneId.UTC)
 * @returns {Instant} The instant at the end of the year (last millisecond)
 */
export const getYearEndInstant = (year: number, zoneId = ZoneId.UTC) => {
  const yearEnd = LocalDate.of(year, 12, 31);
  const zonedDateTime = yearEnd.atTime(23, 59, 59, 999_999_999).atZone(zoneId);
  return zonedDateTime.toInstant();
};
